// 이벤트 단위 시트들 — 일정 종료 등.
//
// 시트 자체는 ui/sheet의 openSheet/closeSheet에 위임하고, 폼 동작과
// API 호출만 모음. 종료 후 rerender는 호출 측이 onCompleted 콜백으로
// 주입한다 (legacy.ts의 render()에 직접 의존하면 순환 import).

import { useMemo, useState } from "preact/hooks";
import { api } from "../api";
import { isAdmin, isPlatformAdmin } from "../auth";
import { matchPlayerName } from "../matches";
import { fetchMembers, state } from "../state";
import { h, icon } from "../ui/dom";
import { mountPreact } from "../ui/preact-mount";
import {
  addMinutesToLocalISO,
  buildSurfaceSelect,
  courtLetter,
  kstTodayStr,
  nextDateForDayOfWeek,
  surfaceLabel,
} from "../ui/format";
import {
  buildMiniTimePicker,
  buildNumberStepper,
  buildSegment,
  buildTimePicker,
} from "../ui/forms";
import { closeSheet, openMixedInfeasibleDialog, openSheet, toast, withBusy } from "../ui/sheet";
import { buildDateTimes, buildOvernightBadge, buildScheduleSummary } from "./index";

/**
 * 내 모임 명단 파서 — 자유 텍스트 → [{ display_name, gender_snapshot }]
 *
 * 한 줄에 한 명. 줄 안에서 쉼표/탭/공백으로 이름과 성별 분리.
 * 성별 토큰: 남|여|m|f|male|female (대소문자 무시).
 *   "김민수, 남"   → { name: "김민수", gender: "male" }
 *   "이지훈"       → { name: "이지훈", gender: null }
 *   "박하늘  여"   → { name: "박하늘", gender: "female" }
 */
function parsePersonalRoster(raw: string): Array<{ display_name: string; gender_snapshot: string | null }> {
  const out: Array<{ display_name: string; gender_snapshot: string | null }> = [];
  const lines = raw.split(/\r?\n/);
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    const tokens = line.split(/[,\t]+|\s{2,}/).map((t) => t.trim()).filter(Boolean);
    let name = tokens[0] || "";
    let gender: string | null = null;
    for (let i = 1; i < tokens.length; i += 1) {
      const g = normalizeGenderToken(tokens[i]);
      if (g) { gender = g; break; }
    }
    if (!name) {
      // 단일 토큰 + 성별 키워드 가능성 — fallback
      const parts = line.split(/\s+/).map((t) => t.trim()).filter(Boolean);
      if (parts.length >= 2) {
        const g = normalizeGenderToken(parts[parts.length - 1]);
        if (g) {
          name = parts.slice(0, -1).join(" ");
          gender = g;
        } else {
          name = parts.join(" ");
        }
      } else {
        name = line;
      }
    } else if (!gender) {
      // 이름에 공백이 있고 마지막 토큰이 성별이면 분리
      const parts = name.split(/\s+/);
      if (parts.length >= 2) {
        const g = normalizeGenderToken(parts[parts.length - 1]);
        if (g) {
          name = parts.slice(0, -1).join(" ");
          gender = g;
        }
      }
    }
    if (name) out.push({ display_name: name, gender_snapshot: gender });
  }
  return out;
}

function normalizeGenderToken(token: string): string | null {
  const t = token.trim().toLowerCase();
  if (t === "남" || t === "m" || t === "male") return "male";
  if (t === "여" || t === "f" || t === "female") return "female";
  return null;
}

export const MATCH_TYPE_OPTIONS: Array<{ value: string; label: string; hint?: string }> = [
  { value: "men_doubles", label: "남복" },
  { value: "women_doubles", label: "여복" },
  { value: "mixed_doubles", label: "혼복" },
  { value: "exceptional_mix", label: "잡복", hint: "성비가 안 맞을 때 인당 게임수를 맞추려고 사용해요 (예: 남3/여1, 여3/남1)" },
];

export interface CompleteEventInfo {
  event_id: string;
}

export interface CompleteMatch {
  result_status?: string | null;
}

interface CompleteTotals {
  entered: number;
  total: number;
  skipped: number;
  incomplete: number;
}

function CompleteEventBody({ totals }: { totals: CompleteTotals }) {
  const { entered, total, skipped, incomplete } = totals;
  const summaryClass =
    incomplete > 0 ? "schedule-summary schedule-summary--warn" : "schedule-summary";
  return (
    <div class="stack">
      <div class={summaryClass}>
        <div style="font-weight:700;margin-bottom:4px">
          {entered}/{total} 매치 점수 입력됨
        </div>
        {skipped > 0 && <div>미진행 처리 {skipped}건</div>}
        {incomplete > 0 && <div>점수 미입력 {incomplete}건</div>}
      </div>
      <ul style="padding-left:18px;margin:12px 0;color:var(--ink-soft);font-size:13px;line-height:1.6">
        <li>종료 후에는 점수 입력·수정이 불가능해요</li>
        <li>참가자 출결·선수 교체도 불가능해요</li>
        <li>통계는 입력된 점수 기준으로 확정돼요</li>
      </ul>
      {incomplete > 0 && (
        <label
          class="field"
          style="display:flex;gap:10px;align-items:flex-start;padding:12px;background:#fee2e2;border-radius:10px;cursor:pointer"
        >
          <input type="checkbox" class="ack-check" style="margin-top:2px" />
          <span style="color:var(--danger);font-size:13px;line-height:1.45">
            미입력 {incomplete}건이 있어요. 그대로 종료할게요.
          </span>
        </label>
      )}
    </div>
  );
}

function CompleteEventFooter({
  ev,
  incomplete,
  onCompleted,
}: {
  ev: CompleteEventInfo;
  incomplete: number;
  onCompleted: () => void;
}) {
  return (
    <>
      <button
        class="btn btn--danger btn--block"
        type="button"
        disabled={incomplete > 0}
        style={incomplete > 0 ? "opacity:0.5" : undefined}
        onClick={async (e: Event) => {
          const btn = e.currentTarget as HTMLButtonElement;
          try {
            await withBusy(btn, "종료 중…", () =>
              api(`/api/v1/events/${ev.event_id}/complete`, { method: "POST", body: {} }),
            );
            closeSheet();
            toast("일정을 종료했어요");
            onCompleted();
          } catch (err: any) {
            toast(err.message);
          }
        }}
      >
        일정 종료
      </button>
      <button
        class="btn btn--ghost btn--block"
        type="button"
        style="margin-top:8px"
        onClick={() => closeSheet()}
      >
        취소
      </button>
    </>
  );
}

export function openCompleteEventSheet(
  ev: CompleteEventInfo,
  matches: CompleteMatch[],
  onCompleted: () => void,
): void {
  const total = matches.length;
  const entered = matches.filter(
    (m) => m.result_status === "entered" || m.result_status === "edited",
  ).length;
  const skipped = matches.filter((m) => m.result_status === "skipped").length;
  const incomplete = total - entered - skipped;

  const bodyEl = mountPreact(
    <CompleteEventBody totals={{ entered, total, skipped, incomplete }} />,
  );
  const footerEl = mountPreact(
    <CompleteEventFooter ev={ev} incomplete={incomplete} onCompleted={onCompleted} />,
  );

  if (incomplete > 0) {
    const check = bodyEl.querySelector(".ack-check") as HTMLInputElement | null;
    const confirmBtn = footerEl.querySelector(".btn--danger") as HTMLButtonElement | null;
    if (check && confirmBtn) {
      check.addEventListener("change", () => {
        confirmBtn.disabled = !check.checked;
        confirmBtn.style.opacity = check.checked ? "" : "0.5";
      });
    }
  }

  openSheet("일정 종료", bodyEl, footerEl);
}

export interface AddMatchEvent {
  event_id: string;
}

export interface AddMatchCourt {
  event_court_id: string;
  court_label?: string | null;
  surface_type?: string | null;
  is_active?: boolean | string;
}

export interface AddMatchParticipant {
  event_participant_id: string;
  display_name?: string | null;
  gender_snapshot?: string | null;
}

function AddMatchBody({
  ev,
  participants,
  courts,
  existingBonusRounds,
  initialCourtId,
  initialCourtLabel,
  onAdded,
}: {
  ev: AddMatchEvent;
  participants: AddMatchParticipant[];
  courts: AddMatchCourt[];
  existingBonusRounds: number[];
  initialCourtId?: string | null;
  initialCourtLabel?: string | null;
  onAdded: () => void;
}) {
  const [sideA, setSideA] = useState<string[]>([]);
  const [sideB, setSideB] = useState<string[]>([]);
  const [chosenCourtId, setChosenCourtId] = useState<string | null>(initialCourtId || null);
  const [chosenCourtLabel, setChosenCourtLabel] = useState<string | null>(initialCourtLabel || null);
  const [customCourt, setCustomCourt] = useState<string>("");
  const [showCustomCourt, setShowCustomCourt] = useState<boolean>(false);
  const [chosenRoundNumber, setChosenRoundNumber] = useState<number | null>(null);

  const activeCourts = useMemo(
    () =>
      courts.filter(
        (c) => String(c.is_active) !== "false" && String(c.is_active) !== "False",
      ),
    [courts],
  );

  const sortedParticipants = useMemo(
    () =>
      [...participants].sort((a, b) => {
        const ga = a.gender_snapshot === "male" ? 0 : 1;
        const gb = b.gender_snapshot === "male" ? 0 : 1;
        return ga - gb || (a.display_name || "").localeCompare(b.display_name || "");
      }),
    [participants],
  );

  const courtOk = !!(chosenCourtId || (chosenCourtLabel && chosenCourtLabel.trim()));
  const submitDisabled = !courtOk || sideA.length !== 2 || sideB.length !== 2;

  function pickCourt(c: AddMatchCourt) {
    setChosenCourtId(c.event_court_id);
    setChosenCourtLabel(c.court_label || null);
    setShowCustomCourt(false);
    setCustomCourt("");
  }

  function pickOtherCourt() {
    setChosenCourtId(null);
    setChosenCourtLabel(null);
    setShowCustomCourt(true);
  }

  function togglePlayer(pid: string) {
    const inA = sideA.includes(pid);
    const inB = sideB.includes(pid);
    if (inA) {
      const nextA = sideA.filter((p) => p !== pid);
      setSideA(nextA);
      if (sideB.length < 2) setSideB([...sideB, pid]);
    } else if (inB) {
      setSideB(sideB.filter((p) => p !== pid));
    } else if (sideA.length < 2) {
      setSideA([...sideA, pid]);
    } else if (sideB.length < 2) {
      setSideB([...sideB, pid]);
    }
  }

  function chipClassFor(pid: string): string {
    const inA = sideA.includes(pid);
    const inB = sideB.includes(pid);
    const cls = ["chip"];
    if (inA) cls.push("chip--side-a", "chip--active");
    else if (inB) cls.push("chip--side-b", "chip--active");
    return cls.join(" ");
  }

  function placeholderText(side: string[]): string {
    return side.length === 0 ? "선수 2명 선택" : "1명 더 선택";
  }

  async function onSubmit(e: Event) {
    const btn = e.currentTarget as HTMLButtonElement;
    const reqBody: any = { side_a: sideA, side_b: sideB };
    if (chosenCourtId) reqBody.event_court_id = chosenCourtId;
    if (chosenCourtLabel) reqBody.court_label = chosenCourtLabel;
    if (!chosenCourtId && customCourt.trim()) reqBody.court_label = customCourt.trim();
    if (chosenRoundNumber) reqBody.round_number = chosenRoundNumber;
    try {
      await withBusy(btn, "추가 중…", () =>
        api(`/api/v1/events/${ev.event_id}/matches/adhoc`, {
          method: "POST",
          body: reqBody,
        }),
      );
      closeSheet();
      toast("매치가 추가되었어요");
      onAdded();
    } catch (err: any) {
      toast(err.message);
    }
  }

  return (
    <div class="stack">
      <p class="field__label">코트</p>
      <div class="chip-group">
        {activeCourts.map((c) => {
          const sf = c.surface_type ? surfaceLabel(c.surface_type) : null;
          const label = sf ? `${c.court_label} · ${sf}` : c.court_label || "코트";
          const active = chosenCourtId === c.event_court_id;
          return (
            <button
              type="button"
              key={c.event_court_id}
              class={"chip" + (active ? " chip--active" : "")}
              onClick={() => pickCourt(c)}
            >
              {label}
            </button>
          );
        })}
        <button
          type="button"
          class={"chip" + (showCustomCourt ? " chip--active" : "")}
          onClick={pickOtherCourt}
        >
          기타
        </button>
      </div>
      {showCustomCourt && (
        <input
          class="input"
          type="text"
          placeholder="코트 이름 입력"
          style="margin-top:8px"
          value={customCourt}
          onInput={(e) => {
            const v = (e.currentTarget as HTMLInputElement).value;
            setCustomCourt(v);
            setChosenCourtLabel(v.trim() || null);
          }}
        />
      )}

      {existingBonusRounds.length > 0 && (
        <>
          <p class="field__label" style="margin-top:12px">라운드</p>
          <div class="chip-group">
            <button
              type="button"
              class={"chip" + (chosenRoundNumber === null ? " chip--active" : "")}
              onClick={() => setChosenRoundNumber(null)}
            >
              새 라운드
            </button>
            {existingBonusRounds.map((rn) => (
              <button
                type="button"
                key={rn}
                class={"chip" + (chosenRoundNumber === rn ? " chip--active" : "")}
                onClick={() => setChosenRoundNumber(rn)}
              >
                보너스 R{rn}
              </button>
            ))}
          </div>
        </>
      )}

      <p class="field__label" style="margin-top:12px">사이드 A</p>
      <div class="chip-group">
        {sideA.map((pid) => {
          const p = participants.find((pp) => pp.event_participant_id === pid);
          return p ? (
            <span key={pid} class="chip chip--side-a">{matchPlayerName(p)}</span>
          ) : null;
        })}
        {sideA.length < 2 && (
          <span class="chip chip--placeholder">{placeholderText(sideA)}</span>
        )}
      </div>

      <p class="field__label" style="margin-top:8px">사이드 B</p>
      <div class="chip-group">
        {sideB.map((pid) => {
          const p = participants.find((pp) => pp.event_participant_id === pid);
          return p ? (
            <span key={pid} class="chip chip--side-b">{matchPlayerName(p)}</span>
          ) : null;
        })}
        {sideB.length < 2 && (
          <span class="chip chip--placeholder">{placeholderText(sideB)}</span>
        )}
      </div>

      <p class="field__label" style="margin-top:12px">참가자 목록</p>
      <div class="chip-group">
        {sortedParticipants.map((p) => {
          const pid = p.event_participant_id;
          const gTag =
            p.gender_snapshot === "male" ? "[남]" : p.gender_snapshot === "female" ? "[여]" : "";
          return (
            <button
              type="button"
              key={pid}
              class={chipClassFor(pid)}
              onClick={() => togglePlayer(pid)}
            >
              {gTag} {matchPlayerName(p)}
            </button>
          );
        })}
      </div>

      <button
        class="btn btn--primary btn--block"
        type="button"
        disabled={submitDisabled}
        style="margin-top:16px"
        onClick={onSubmit}
      >
        매치 추가
      </button>
    </div>
  );
}

export interface OpenAddMatchOpts {
  initialCourtId?: string | null;
  initialCourtLabel?: string | null;
}

export function openAddMatchSheet(
  ev: AddMatchEvent,
  participants: AddMatchParticipant[],
  courts: AddMatchCourt[],
  existingBonusRounds: number[],
  onAdded: () => void,
  opts: OpenAddMatchOpts = {},
): void {
  const bodyEl = mountPreact(
    <AddMatchBody
      ev={ev}
      participants={participants}
      courts={courts}
      existingBonusRounds={existingBonusRounds}
      initialCourtId={opts.initialCourtId}
      initialCourtLabel={opts.initialCourtLabel}
      onAdded={onAdded}
    />,
  );
  openSheet("매치 추가", bodyEl);
}

export interface EditEvent {
  event_id: string;
  title?: string | null;
  event_date?: string | null;
  location?: string | null;
  mode?: string | null;
  status?: string | null;
  daily_host_member_id?: string | null;
  court_booking_start_at?: string | null;
  court_booking_end_at?: string | null;
  max_round_count?: number | null;
  warmup_duration_minutes?: number | null;
  match_duration_minutes?: number | null;
  break_duration_minutes?: number | null;
}

export interface EditEventCourt {
  court_label?: string | null;
  surface_type?: string | null;
  available_from_at?: string | null;
  available_until_at?: string | null;
}

export interface EditEventParticipant {
  member_id?: string | null;
  display_name?: string | null;
  participant_type?: string | null;
  participant?: any;
}

interface EditEventCallbacks {
  onSaved: () => void;
  onDeleted: () => void;
}

export async function openEditEventSheet(
  ev: EditEvent,
  existingCourts: EditEventCourt[],
  eventParticipants: EditEventParticipant[],
  cb: EditEventCallbacks,
): Promise<void> {
  // 이전엔 일일 호스트 picker에서 운영진 제외용으로 fetchMembers + staff 셋을
  // 만들었지만 호스트 picker 폐지로 dead. 필요해지면 복원.

  const titleInput = h("input", { class: "input", type: "text", value: ev.title || "", required: true }) as HTMLInputElement;
  const dateInput = h("input", { class: "input", type: "date", value: ev.event_date || "" }) as HTMLInputElement;
  const locationInput = h("input", { class: "input", type: "text", value: ev.location || "", placeholder: "예: 양재 테니스장" }) as HTMLInputElement;

  const existStartHH = ev.court_booking_start_at ? ev.court_booking_start_at.slice(11, 13) : "10";
  const existStartMM = ev.court_booking_start_at ? ev.court_booking_start_at.slice(14, 16) : "00";
  const existEndHH = ev.court_booking_end_at ? ev.court_booking_end_at.slice(11, 13) : "12";
  const existEndMM = ev.court_booking_end_at ? ev.court_booking_end_at.slice(14, 16) : "00";
  const startTime = buildTimePicker(existStartHH, existStartMM);
  const endTime = buildTimePicker(existEndHH, existEndMM);

  const modeHidden = h("input", { type: "hidden", value: ev.mode || "kdk" }) as HTMLInputElement;
  const modeSeg = buildSegment(
    [["kdk", "개인전"], ["team", "팀전"]],
    modeHidden.value,
    (val: string) => { modeHidden.value = val; },
  );

  const courtEntries: Array<{ name: string; fromAt: string | null; untilAt: string | null; surface: string | null }> = [];
  const courtListEl = h("div", { class: "court-inputs" });
  function addCourtInput(name = "", fromAt: string | null = null, untilAt: string | null = null, surface: string | null = null) {
    courtEntries.push({ name, fromAt, untilAt, surface });
    rebuildCourtListEdit();
  }
  function rebuildCourtListEdit() {
    courtListEl.innerHTML = "";
    courtEntries.forEach((entry, i) => {
      const inp = h("input", {
        class: "input input--compact", type: "text",
        value: entry.name, placeholder: courtLetter(i),
        oninput: (e: Event) => { entry.name = (e.target as HTMLInputElement).value; },
      });
      const surfaceSel = buildSurfaceSelect(entry.surface, (v: string | null) => { entry.surface = v; });
      const hasWindow = !!(entry.fromAt || entry.untilAt);
      const windowRow = h("div", { class: "court-row__window" }) as HTMLElement;
      windowRow.hidden = !hasWindow;
      const fromSel = buildMiniTimePicker(entry.fromAt, (v: string | null) => { entry.fromAt = v; });
      const untilSel = buildMiniTimePicker(entry.untilAt, (v: string | null) => { entry.untilAt = v; });
      windowRow.append(
        h("span", { class: "court-row__window-label" }, "시간대"),
        fromSel.el, h("span", { class: "court-row__window-sep" }, "–"), untilSel.el,
        h("button", {
          type: "button", class: "court-row__remove", "aria-label": "시간대 해제",
          onclick: () => { entry.fromAt = null; entry.untilAt = null; windowRow.hidden = true; },
        }, icon("x", 12)),
      );
      const row = h("div", { class: "court-row court-row--segment" }, [
        h("div", { class: "court-row__top" }, [
          inp,
          surfaceSel,
          !hasWindow ? h("button", {
            type: "button", class: "court-row__clock", "aria-label": "시간대 설정",
            onclick: () => {
              windowRow.hidden = false;
              const sv = startTime.getValue();
              const ev2 = endTime.getValue();
              if (!entry.fromAt) { entry.fromAt = sv; fromSel.setValue(sv); }
              if (!entry.untilAt) { entry.untilAt = ev2; untilSel.setValue(ev2); }
            },
          }, "⏱") : null,
          courtEntries.length > 1 ? h("button", {
            type: "button", class: "court-row__remove", "aria-label": "삭제",
            onclick: () => { courtEntries.splice(i, 1); rebuildCourtListEdit(); },
          }, icon("x", 14)) : null,
        ]),
        windowRow,
      ]);
      courtListEl.append(row);
    });
  }
  function getCourtList() {
    const date = dateInput.value || ev.event_date || "";
    return courtEntries.map((entry, i) => {
      const out: any = {
        court_label: entry.name.trim() || courtLetter(i),
        court_order: i + 1,
      };
      if (entry.surface) out.surface_type = entry.surface;
      if (entry.fromAt) out.available_from_at = `${date}T${entry.fromAt}:00`;
      if (entry.untilAt) {
        const untilDate = (entry.untilAt <= (entry.fromAt || startTime.getValue()))
          ? (() => {
              const d = new Date(date + "T00:00:00");
              d.setDate(d.getDate() + 1);
              const p = (n: number) => String(n).padStart(2, "0");
              return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
            })()
          : date;
        out.available_until_at = `${untilDate}T${entry.untilAt}:00`;
      }
      return out;
    });
  }
  if (existingCourts.length > 0) {
    for (const c of existingCourts) {
      const fAt = c.available_from_at ? c.available_from_at.slice(11, 16) : null;
      const uAt = c.available_until_at ? c.available_until_at.slice(11, 16) : null;
      addCourtInput(c.court_label || "", fAt, uAt, c.surface_type || null);
    }
  } else {
    for (let i = 0; i < 4; i++) addCourtInput("");
  }
  const addCourtBtn = h("button", {
    type: "button", class: "btn btn--ghost btn--sm",
    onclick: () => addCourtInput(""),
  }, "+ 코트 추가");

  // 일일 호스트는 deprecated. 이제 정기모임 만든 사람이 자동으로 호스트.
  // 별도 picker 제거. 기존 데이터의 daily_host_member_id는 backend grandfather.

  const rounds = buildNumberStepper(ev.max_round_count || 6, 1, 30, 1, "라운드");
  const warmupMin = buildNumberStepper(ev.warmup_duration_minutes ?? 10, 0, 30, 1, "분");
  const matchMin = buildNumberStepper(ev.match_duration_minutes || 35, 10, 90, 1, "분");
  const breakMin = buildNumberStepper(ev.break_duration_minutes || 5, 0, 30, 1, "분");

  const titleOnly = ev.status === "in_progress";
  const canDelete = ev.status === "draft" || ev.status === "ready";

  const form = h("form", {
    class: "form",
    onsubmit: async (e: Event) => {
      e.preventDefault();
      const body: any = { title: titleInput.value.trim() };
      if (!titleOnly) {
        const date = dateInput.value || ev.event_date;
        if (dateInput.value) body.event_date = dateInput.value;
        body.mode = modeHidden.value;
        body.location = locationInput.value.trim() || null;
        if (date) {
          const times = buildDateTimes(date, startTime.getValue(), endTime.getValue());
          body.court_booking_start_at = times.startISO;
          body.first_match_start_at = times.startISO;
          body.court_booking_end_at = times.endISO;
        }
        body.max_round_count = rounds.getValue();
        body.warmup_duration_minutes = warmupMin.getValue();
        body.match_duration_minutes = matchMin.getValue();
        body.break_duration_minutes = breakMin.getValue();
      }
      if (!body.title) { toast("일정명을 입력해주세요"); return; }
      if (!titleOnly && body.court_booking_start_at && body.court_booking_end_at) {
        const totalMin = (new Date(body.court_booking_end_at).getTime() - new Date(body.court_booking_start_at).getTime()) / 60000;
        const neededMin = body.max_round_count * (body.match_duration_minutes + body.break_duration_minutes) - body.break_duration_minutes;
        if (totalMin > 0 && neededMin > totalMin) {
          toast(`${body.max_round_count}R × ${body.match_duration_minutes}분 = ${neededMin}분인데, 전체 시간은 ${totalMin}분이에요`);
          return;
        }
      }
      if (!titleOnly && body.match_duration_minutes && body.match_duration_minutes <= 25) {
        if (!confirm(`경기 시간이 ${body.match_duration_minutes}분으로 짧아요. 라운드 수(${body.max_round_count})가 맞는지 확인해주세요. 이대로 진행할까요?`)) {
          return;
        }
      }
      try {
        await api(`/api/v1/events/${ev.event_id}`, { method: "PATCH", body });
        if (!titleOnly) {
          await api(`/api/v1/events/${ev.event_id}/courts`, { method: "PUT", body: { courts: getCourtList() } });
        }
        closeSheet();
        toast("저장했어요");
        cb.onSaved();
      } catch (err: any) { toast(err.message); }
    },
  }, [
    h("div", { class: "field" }, [h("span", { class: "field__label" }, "일정명"), titleInput]),
    !titleOnly && h("div", { class: "field" }, [h("span", { class: "field__label" }, "날짜"), dateInput]),
    !titleOnly && h("div", { class: "field" }, [
      h("span", { class: "field__label" }, "모드"),
      modeHidden,
      modeSeg,
    ]),
    !titleOnly && h("div", { class: "field" }, [
      h("span", { class: "field__label" }, "장소 (선택)"),
      locationInput,
    ]),
    !titleOnly && h("div", { class: "field" }, [
      h("span", { class: "field__label" }, "코트"),
      courtListEl,
      addCourtBtn,
    ]),
    !titleOnly && h("div", { class: "field-row" }, [
      h("div", { class: "field field--half" }, [h("span", { class: "field__label" }, "시작 시간"), startTime.el]),
      h("div", { class: "field field--half" }, [h("span", { class: "field__label" }, ["종료 시간", buildOvernightBadge(startTime, endTime)]), endTime.el]),
    ]),
    !titleOnly && h("div", { class: "field" }, [h("span", { class: "field__label" }, "라운드 수"), rounds.el]),
    !titleOnly && h("div", { class: "field-row field-row--dense" }, [
      h("div", { class: "field field--half" }, [h("span", { class: "field__label" }, "웜업"), warmupMin.el]),
      h("div", { class: "field field--half" }, [h("span", { class: "field__label" }, "경기"), matchMin.el]),
      h("div", { class: "field field--half" }, [h("span", { class: "field__label" }, "휴게"), breakMin.el]),
    ]),
    !titleOnly && h("span", { class: "field__hint" }, "웜업: 일정 시작 후 첫 경기 전 몸풀기 시간"),
    !titleOnly && buildScheduleSummary(startTime, endTime, rounds, matchMin, breakMin, warmupMin),
    titleOnly && h("p", { class: "muted" }, "일정 진행 중에는 일정명만 수정할 수 있어요."),
    h("button", { class: "btn btn--primary btn--block", type: "submit" }, "저장"),
    canDelete && h("div", { class: "danger-zone" }, [
      h("button", {
        class: "btn btn--danger btn--block",
        type: "button",
        onclick: async () => {
          if (!confirm(`"${ev.title}" 일정을 삭제할까요? 이 작업은 되돌릴 수 없어요.`)) return;
          try {
            await api(`/api/v1/events/${ev.event_id}`, { method: "DELETE" });
            closeSheet();
            toast("일정을 삭제했어요");
            cb.onDeleted();
          } catch (err: any) { toast(err.message); }
        },
      }, "일정 삭제"),
      h("p", { class: "field__hint" }, "시작 전(draft/ready) 상태에서만 삭제할 수 있어요."),
    ]),
  ]);

  openSheet("일정 수정", form);
}

export interface NewEventCallbacks {
  onCreated: (newEventId: string) => void;
  // Admin-only template hooks; both omitted for non-admin.
  buildTemplatePicker?: (applyFn: (t: any) => void) => HTMLElement | null;
  openSaveAsTemplatePromptSheet?: (preset: any) => void;
}

function applyTemplateToNewEventForm(
  t: any,
  refs: {
    titleInput: HTMLInputElement;
    dateInput: HTMLInputElement;
    locationInput: HTMLInputElement;
    startTime: any;
    endTime: any;
    rounds: any;
    matchMin: any;
    breakMin: any;
    warmupMin: any;
    kindHidden: HTMLInputElement;
    kindSeg: HTMLElement;
    courtEntries: Array<{ name: string; fromAt: string | null; untilAt: string | null; surface: string | null }>;
    rebuildCourtList: () => void;
  },
): void {
  const {
    titleInput, dateInput, locationInput, startTime, endTime,
    rounds, matchMin, breakMin, warmupMin, kindHidden, kindSeg,
    courtEntries, rebuildCourtList,
  } = refs;
  if (t.title_pattern) titleInput.value = t.title_pattern;
  else if (t.name) titleInput.value = t.name;
  if (t.day_of_week !== null && t.day_of_week !== undefined) {
    const next = nextDateForDayOfWeek(t.day_of_week);
    if (typeof next === "string") dateInput.value = next;
  }
  if (t.location) locationInput.value = t.location;
  if (t.start_time) startTime.setValue(t.start_time);
  if (t.end_time) endTime.setValue(t.end_time);
  if (typeof t.max_round_count === "number" && rounds) rounds.setValue(t.max_round_count);
  if (typeof t.match_duration_minutes === "number") matchMin.setValue(t.match_duration_minutes);
  if (typeof t.break_duration_minutes === "number") breakMin.setValue(t.break_duration_minutes);
  if (typeof t.warmup_duration_minutes === "number") warmupMin.setValue(t.warmup_duration_minutes);
  if (t.courts && t.courts.length > 0) {
    courtEntries.length = 0;
    for (const c of t.courts) {
      courtEntries.push({
        name: c.court_label || "",
        fromAt: c.available_from || null,
        untilAt: c.available_until || null,
        surface: c.surface_type || null,
      });
    }
    rebuildCourtList();
  }
  let kindVal: string | null = null;
  if (t.mode === "team") kindVal = "team";
  else if (t.event_type === "regular") kindVal = "regular";
  else if (t.mode === "kdk") kindVal = "special_kdk";
  if (kindVal && kindHidden.value !== kindVal) {
    kindHidden.value = kindVal;
    const btns = kindSeg.querySelectorAll(".segment__btn");
    btns.forEach((b) => b.classList.remove("segment__btn--active"));
    const order = ["regular", "special_kdk", "team"];
    const idx = order.indexOf(kindVal);
    if (idx >= 0 && btns[idx]) btns[idx].classList.add("segment__btn--active");
  }
}

export async function openNewEventSheet(cb: NewEventCallbacks): Promise<void> {
  let allEvents: any[] = [];
  try {
    allEvents = (await api("/api/v1/events")) || [];
  } catch (_) {
    allEvents = [];
  }
  function kindKey(ev: any): string {
    // personal "내 모임"은 visibility 기준으로 별도 kind. event_type=regular이지만
    // 명단/통계가 분리되므로 prefill 후보도 같이 분리해야 의미 있음.
    if (ev.event_visibility === "personal") return "personal";
    if (ev.event_type === "regular") return "regular";
    if (ev.mode === "team") return "team";
    return "special_kdk";
  }
  function pickLatestByKind(kind: string) {
    const candidates = allEvents
      .filter((e) => kindKey(e) === kind)
      .sort(
        (a, b) =>
          (b.event_date || "").localeCompare(a.event_date || "") ||
          (b.created_at || "").localeCompare(a.created_at || ""),
      );
    return candidates[0] || null;
  }

  // 본인이 직전에 만든 이벤트 kind를 default로 — 동일 패턴으로 또 만드는 케이스
  // (정기 → 정기 / 내 모임 → 내 모임)에서 prefill이 자연스럽게 매칭됨.
  const myMemberId = state.session?.actor?.member_id;
  const myEventsSorted = allEvents
    .filter((e: any) => e.created_by && e.created_by === myMemberId)
    .sort((a: any, b: any) =>
      (b.event_date || "").localeCompare(a.event_date || "") ||
      (b.created_at || "").localeCompare(a.created_at || ""),
    );
  const initialKind = myEventsSorted[0] ? kindKey(myEventsSorted[0]) : "regular";

  let prefill: any = null;
  const kindLatest = pickLatestByKind(initialKind);
  if (kindLatest) {
    try { prefill = await api(`/api/v1/events/${kindLatest.event_id}`); }
    catch (_) { prefill = null; }
  }

  // personal 명단 prefill — initialKind가 personal이 아니어도, 사용자가 picker에서
  // "내 모임" 선택할 수 있으니 가장 최근 personal latest를 별도로 미리 받아둔다.
  let personalRosterSource: any = null;
  if (isPlatformAdmin()) {
    if (prefill?.event?.event_visibility === "personal") {
      personalRosterSource = prefill;
    } else {
      const personalLatest = pickLatestByKind("personal");
      if (personalLatest) {
        try { personalRosterSource = await api(`/api/v1/events/${personalLatest.event_id}`); }
        catch (_) { personalRosterSource = null; }
      }
    }
  }
  const personalRosterPrefill = ((personalRosterSource?.participants || []) as any[])
    .filter((p) => !p.member_id)
    .map((p) => {
      const name = (p.display_name || "").trim();
      const g = p.gender_snapshot;
      const gLabel = g === "male" ? "남" : g === "female" ? "여" : "";
      return gLabel ? `${name}, ${gLabel}` : name;
    })
    .filter((line) => line.trim())
    .join("\n");

  const prevEvent = prefill?.event || null;
  const prevCourts = prefill?.courts || [];
  const prevParticipants = prefill?.participants || [];

  const titleInput = h("input", { class: "input", type: "text", placeholder: "예: 4월 정기 모임", value: prevEvent?.title || "", required: true }) as HTMLInputElement;
  const dateInput = h("input", { class: "input", type: "date", value: kstTodayStr(), required: true }) as HTMLInputElement;
  const locationInput = h("input", { class: "input", type: "text", placeholder: "예: 양재 테니스장", value: prevEvent?.location || "" }) as HTMLInputElement;

  const prevStart = prevEvent?.court_booking_start_at;
  const prevEnd = prevEvent?.court_booking_end_at;
  const startHH = prevStart ? prevStart.slice(11, 13) : "10";
  const startMM = prevStart ? prevStart.slice(14, 16) : "00";
  const endHH = prevEnd ? prevEnd.slice(11, 13) : "12";
  const endMM = prevEnd ? prevEnd.slice(14, 16) : "00";
  const startTime = buildTimePicker(startHH, startMM);
  const endTime = buildTimePicker(endHH, endMM);

  // initialKind가 personal인데 권한이 사라진 경우(드문 케이스) regular로 fallback.
  const initialKindEffective = (initialKind === "personal" && !isPlatformAdmin()) ? "regular" : initialKind;
  const kindHidden = h("input", { type: "hidden", value: initialKindEffective }) as HTMLInputElement;
  let isFriendlyMatch = false;
  const friendlyToggle = h("label", { class: "toggle-row" }, [
    h("span", { class: "toggle-row__label" }, "교류전"),
    h("input", { type: "checkbox", class: "toggle", onchange: (e: Event) => {
      isFriendlyMatch = (e.target as HTMLInputElement).checked;
      opponentField.hidden = !isFriendlyMatch;
      teamNamesField.hidden = isFriendlyMatch || kindHidden.value !== "team";
    }}),
  ]) as HTMLElement;
  friendlyToggle.hidden = true;
  const opponentField = h("div", { class: "field" }, [
    h("span", { class: "field__label" }, "상대 클럽명"),
    h("input", { class: "input", type: "text", placeholder: "상대 클럽명", "data-opponent": "name" }),
  ]) as HTMLElement;
  opponentField.hidden = true;
  const defaultTeamA = state.club?.default_team_a_name || "";
  const defaultTeamB = state.club?.default_team_b_name || "";
  const teamNamesField = h("div", { class: "field-row" }, [
    h("div", { class: "field field--half" }, [
      h("span", { class: "field__label" }, "팀 A 이름"),
      h("input", { class: "input", type: "text", value: defaultTeamA, placeholder: "A", "data-team": "a" }),
    ]),
    h("div", { class: "field field--half" }, [
      h("span", { class: "field__label" }, "팀 B 이름"),
      h("input", { class: "input", type: "text", value: defaultTeamB, placeholder: "B", "data-team": "b" }),
    ]),
  ]) as HTMLElement;
  teamNamesField.hidden = true;
  // "내 모임"은 v1 platform_admin 도그푸딩 단계라 페어매치팀(개발자)만 노출.
  // v1.1에서 일반 회원으로 풀 예정. 풀 때는 isPlatformAdmin() → isMember()
  // 또는 그냥 옵션을 무조건 노출하면 됨.
  const showPersonalOption = isPlatformAdmin();
  const kindSegOptions: Array<[string, string]> = [
    ["regular", "정기 모임"],
    ...(showPersonalOption ? [["personal", "내 모임"] as [string, string]] : []),
    ["special_kdk", "개인전"],
    ["team", "팀전"],
  ];
  const rosterTextarea = h("textarea", {
    class: "input", rows: 8,
    placeholder: "한 줄에 한 명씩 — 이름만 적어도 OK\n예)\n김민수\n이지훈, 남\n박하늘, 여",
    style: "resize:vertical;min-height:140px;font-family:inherit",
    "data-roster": "personal",
  }) as HTMLTextAreaElement;
  // 가장 최근 personal 이벤트의 게스트 명단을 prefill — 같은 멤버로 또 모이는
  // 케이스가 흔하니 매번 8명 다시 적는 부담을 덜어준다. 사용자는 일부 줄만
  // 수정해서 저장 가능.
  if (personalRosterPrefill) rosterTextarea.value = personalRosterPrefill;
  const rosterField = h("div", { class: "field" }, [
    h("span", { class: "field__label" }, "명단"),
    rosterTextarea,
    h("span", { class: "field__hint" }, "성별 적으면 매치 타입 자동 분류. 안 적으면 '복식'으로 묶이고, 참가자 카드에서 나중에 채워도 돼요."),
  ]) as HTMLElement;
  // hidden 상태는 initialKindEffective 기반으로 syncKindFields가 정렬한다 (아래).
  function syncKindFields(val: string) {
    kindHidden.value = val;
    const isTeam = val === "team";
    const isPersonal = val === "personal";
    teamNamesField.hidden = !isTeam || isFriendlyMatch;
    friendlyToggle.hidden = !isTeam;
    rosterField.hidden = !isPersonal;
    if (!isTeam) {
      isFriendlyMatch = false;
      opponentField.hidden = true;
      const toggle = friendlyToggle.querySelector("input") as HTMLInputElement | null;
      if (toggle) toggle.checked = false;
    }
  }
  const kindSeg = buildSegment(kindSegOptions, kindHidden.value, syncKindFields);
  // 시트 mount 시 initialKindEffective에 맞춰 hidden 상태 한번 정렬.
  syncKindFields(initialKindEffective);
  function resolveKind() {
    if (kindHidden.value === "team") return { event_type: "special", mode: "team", visibility: "club" };
    if (kindHidden.value === "personal") return { event_type: "regular", mode: "kdk", visibility: "personal" };
    if (kindHidden.value === "regular") return { event_type: "regular", mode: "kdk", visibility: "club" };
    return { event_type: "special", mode: "kdk", visibility: "club" };
  }

  const courtEntries: Array<{ name: string; fromAt: string | null; untilAt: string | null; surface: string | null }> = [];
  const courtListEl = h("div", { class: "court-inputs" });
  function addCourtInput(name = "", fromAt: string | null = null, untilAt: string | null = null, surface: string | null = null) {
    courtEntries.push({ name, fromAt, untilAt, surface });
    rebuildCourtList();
  }
  function rebuildCourtList() {
    courtListEl.innerHTML = "";
    courtEntries.forEach((entry, i) => {
      const inp = h("input", {
        class: "input input--compact", type: "text",
        value: entry.name, placeholder: courtLetter(i),
        oninput: (e: Event) => { entry.name = (e.target as HTMLInputElement).value; },
      });
      const surfaceSel = buildSurfaceSelect(entry.surface, (v: string | null) => { entry.surface = v; });
      const hasWindow = !!(entry.fromAt || entry.untilAt);
      const windowRow = h("div", { class: "court-row__window" }) as HTMLElement;
      windowRow.hidden = !hasWindow;

      const fromSel = buildMiniTimePicker(entry.fromAt, (v: string | null) => { entry.fromAt = v; });
      const untilSel = buildMiniTimePicker(entry.untilAt, (v: string | null) => { entry.untilAt = v; });
      windowRow.append(
        h("span", { class: "court-row__window-label" }, "시간대"),
        fromSel.el, h("span", { class: "court-row__window-sep" }, "–"), untilSel.el,
        h("button", {
          type: "button", class: "court-row__remove", "aria-label": "시간대 해제",
          onclick: () => { entry.fromAt = null; entry.untilAt = null; windowRow.hidden = true; },
        }, icon("x", 12)),
      );

      const row = h("div", { class: "court-row court-row--segment" }, [
        h("div", { class: "court-row__top" }, [
          inp,
          surfaceSel,
          !hasWindow ? h("button", {
            type: "button", class: "court-row__clock", "aria-label": "시간대 설정",
            title: "이 코트가 사용 가능한 시간대 설정",
            onclick: () => {
              windowRow.hidden = false;
              const sv = startTime.getValue();
              const ev = endTime.getValue();
              if (!entry.fromAt) { entry.fromAt = sv; fromSel.setValue(sv); }
              if (!entry.untilAt) { entry.untilAt = ev; untilSel.setValue(ev); }
            },
          }, "⏱") : null,
          courtEntries.length > 1 ? h("button", {
            type: "button", class: "court-row__remove", "aria-label": "삭제",
            onclick: () => { courtEntries.splice(i, 1); rebuildCourtList(); },
          }, icon("x", 14)) : null,
        ]),
        windowRow,
      ]);
      courtListEl.append(row);
    });
  }
  function getCourtList() {
    const date = dateInput.value;
    return courtEntries.map((entry, i) => {
      const out: any = {
        court_label: entry.name.trim() || courtLetter(i),
        court_order: i + 1,
      };
      if (entry.surface) out.surface_type = entry.surface;
      if (entry.fromAt) out.available_from_at = `${date}T${entry.fromAt}:00`;
      if (entry.untilAt) {
        const untilDate = (entry.untilAt <= (entry.fromAt || startTime.getValue()))
          ? (() => {
              const d = new Date(date + "T00:00:00");
              d.setDate(d.getDate() + 1);
              const p = (n: number) => String(n).padStart(2, "0");
              return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
            })()
          : date;
        out.available_until_at = `${untilDate}T${entry.untilAt}:00`;
      }
      return out;
    });
  }
  if (prevCourts.length > 0) {
    for (const c of prevCourts) {
      const fAt = c.available_from_at ? c.available_from_at.slice(11, 16) : null;
      const uAt = c.available_until_at ? c.available_until_at.slice(11, 16) : null;
      addCourtInput(c.court_label || "", fAt, uAt, c.surface_type || null);
    }
  } else {
    for (let i = 0; i < 4; i++) addCourtInput("");
  }
  const addCourtBtn = h("button", {
    type: "button", class: "btn btn--ghost btn--sm",
    onclick: () => addCourtInput(""),
  }, "+ 코트 추가");

  const rounds = buildNumberStepper(prevEvent?.max_round_count || 6, 1, 30, 1, "라운드");
  const matchMin = buildNumberStepper(prevEvent?.match_duration_minutes || 30, 10, 90, 1, "분");
  const breakMin = buildNumberStepper(prevEvent?.break_duration_minutes ?? 5, 0, 30, 1, "분");
  const warmupMin = buildNumberStepper(prevEvent?.warmup_duration_minutes ?? 10, 0, 30, 1, "분");

  let matchManuallySet = false;
  matchMin.onUserChange(() => { matchManuallySet = true; });
  function recomputeMatch() {
    if (matchManuallySet) return;
    const [sh, sm] = startTime.getValue().split(":").map(Number);
    const [eh, em] = endTime.getValue().split(":").map(Number);
    let total = (eh * 60 + em) - (sh * 60 + sm);
    if (total <= 0) total += 24 * 60;
    const r = rounds.getValue();
    const w = warmupMin.getValue();
    const b = breakMin.getValue();
    if (r < 1) return;
    const raw = (total - w + b) / r - b;
    const stepped = Math.max(10, Math.min(90, Math.floor(raw)));
    matchMin.setValue(stepped);
  }
  startTime.onChange(recomputeMatch);
  endTime.onChange(recomputeMatch);
  rounds.onChange(recomputeMatch);
  warmupMin.onChange(recomputeMatch);
  breakMin.onChange(recomputeMatch);
  recomputeMatch();

  const advancedBody = h("div", { class: "stack" }, [
    h("div", { class: "field-row field-row--dense" }, [
      h("div", { class: "field field--half" }, [h("span", { class: "field__label" }, "웜업"), warmupMin.el]),
      h("div", { class: "field field--half" }, [h("span", { class: "field__label" }, "경기"), matchMin.el]),
      h("div", { class: "field field--half" }, [h("span", { class: "field__label" }, "휴게"), breakMin.el]),
    ]),
    h("span", { class: "field__hint" }, "웜업: 일정 시작 후 첫 경기 전 몸풀기 시간"),
  ]) as HTMLElement;
  advancedBody.hidden = true;
  const advancedToggle = h("button", {
    type: "button",
    class: "btn btn--ghost btn--sm",
    onclick: () => {
      advancedBody.hidden = !advancedBody.hidden;
      advancedToggle.textContent = advancedBody.hidden ? "▸ 상세 설정" : "▾ 상세 설정";
    },
  }, "▸ 상세 설정") as HTMLButtonElement;

  const prevMemberIds = prevParticipants
    .filter((p: any) => p.member_id)
    .map((p: any) => p.member_id);

  let loadedFromTemplate = false;
  const submitBtn = h("button", { class: "btn btn--primary btn--block", type: "submit" }, "일정 만들기") as HTMLButtonElement;

  // 호출자(main.ts)에서 권한에 따라 buildTemplatePicker를 주입할지 결정함.
  // 여기선 콜백 유무만 체크 — 회원도 템플릿 적용 가능 (read-only).
  const templatePickerSection = cb.buildTemplatePicker
    ? cb.buildTemplatePicker((t: any) => {
        loadedFromTemplate = true;
        applyTemplateToNewEventForm(t, {
          titleInput, dateInput, locationInput, startTime, endTime,
          rounds, matchMin, breakMin, warmupMin, kindHidden, kindSeg,
          courtEntries, rebuildCourtList,
        });
      })
    : null;

  const form = h("form", {
    class: "form",
    onsubmit: async (e: Event) => {
      e.preventDefault();
      const date = dateInput.value;
      const times = buildDateTimes(date, startTime.getValue(), endTime.getValue());
      const warmup = warmupMin.getValue();
      const firstMatchStart = warmup > 0
        ? addMinutesToLocalISO(times.startISO, warmup)
        : times.startISO;
      const kind = resolveKind();
      const body: any = {
        title: titleInput.value.trim(),
        event_date: date,
        mode: kind.mode,
        event_type: kind.event_type,
        court_booking_start_at: times.startISO,
        court_booking_end_at: times.endISO,
        first_match_start_at: firstMatchStart,
        max_round_count: rounds.getValue(),
        match_duration_minutes: matchMin.getValue(),
        break_duration_minutes: breakMin.getValue(),
        warmup_duration_minutes: warmup,
      };
      if (kind.visibility === "personal") body.event_visibility = "personal";
      // 내 모임 명단 파싱 (이름·성별, 줄바꿈/쉼표/탭 구분)
      let personalRoster: Array<{ display_name: string; gender_snapshot: string | null }> = [];
      if (kind.visibility === "personal") {
        const rosterEl = rosterField.querySelector('[data-roster="personal"]') as HTMLTextAreaElement | null;
        const raw = rosterEl?.value || "";
        personalRoster = parsePersonalRoster(raw);
        if (personalRoster.length < 4) {
          toast("내 모임은 최소 4명 명단이 필요해요");
          return;
        }
      }
      if (locationInput.value.trim()) body.location = locationInput.value.trim();
      if (kind.mode === "team") {
        if (isFriendlyMatch) {
          const oppInput = opponentField.querySelector('[data-opponent="name"]') as HTMLInputElement | null;
          const oppName = oppInput?.value.trim();
          if (!oppName) { toast("상대 클럽명을 입력해주세요"); return; }
          body.is_friendly_match = true;
          body.opponent_club_name = oppName;
          body.team_a_name = state.club?.short_name || state.club?.name || "우리 팀";
          body.team_b_name = oppName;
        } else {
          const aInput = teamNamesField.querySelector('[data-team="a"]') as HTMLInputElement | null;
          const bInput = teamNamesField.querySelector('[data-team="b"]') as HTMLInputElement | null;
          const aName = aInput?.value.trim();
          const bName = bInput?.value.trim();
          if (aName) body.team_a_name = aName;
          if (bName) body.team_b_name = bName;
        }
      }
      if (!body.title || !body.event_date || !body.mode) {
        toast("필수 값을 입력해주세요");
        return;
      }
      const totalMinutes = (new Date(times.endISO).getTime() - new Date(times.startISO).getTime()) / 60000;
      const neededMinutes = warmup + (body.max_round_count * (body.match_duration_minutes + body.break_duration_minutes) - body.break_duration_minutes);
      if (totalMinutes > 0 && neededMinutes > totalMinutes) {
        toast(`웜업 ${warmup}분 + ${body.max_round_count}라운드 × ${body.match_duration_minutes}분 = ${neededMinutes}분인데, 전체 시간은 ${totalMinutes}분이에요`);
        return;
      }
      if (body.match_duration_minutes <= 25) {
        if (!confirm(`경기 시간이 ${body.match_duration_minutes}분으로 짧아요. 라운드 수(${body.max_round_count})가 맞는지 확인해주세요. 이대로 진행할까요?`)) {
          return;
        }
      }
      try {
        await withBusy(submitBtn, "만드는 중…", async () => {
          const res = await api("/api/v1/events", { method: "POST", body });
          const newEventId = res.event.event_id;
          await api(`/api/v1/events/${newEventId}/courts`, { method: "PUT", body: { courts: getCourtList() } });
          // 내 모임은 외부 명단을 게스트로 일괄 등록 (직전 회원 prefill 사용 안 함).
          if (kind.visibility === "personal") {
            for (const g of personalRoster) {
              try {
                await api(`/api/v1/events/${newEventId}/participants/guests`, {
                  method: "POST",
                  body: {
                    display_name: g.display_name,
                    gender_snapshot: g.gender_snapshot,
                  },
                });
              } catch (_) { /* skip individual failure */ }
            }
          } else if (prevMemberIds.length > 0) {
            try {
              await api(`/api/v1/events/${newEventId}/participants/members`, {
                method: "POST",
                body: { member_ids: prevMemberIds },
              });
            } catch (_) { /* non-fatal */ }
          }
          const isRegular = kind.event_type === "regular";
          const isPersonal = kind.visibility === "personal";
          const canPromptTemplate = isAdmin() && isRegular && !isPersonal && !loadedFromTemplate && !!cb.openSaveAsTemplatePromptSheet;
          const templatePreset = canPromptTemplate ? {
            name: "",
            title_pattern: body.title,
            day_of_week: new Date(`${date}T00:00:00`).getDay(),
            start_time: startTime.getValue(),
            end_time: endTime.getValue(),
            location: body.location || null,
            mode: body.mode,
            event_type: body.event_type,
            court_count: courtEntries.length,
            courts: courtEntries.map((e, i) => {
              const out: any = { court_label: e.name.trim() || courtLetter(i) };
              if (e.surface) out.surface_type = e.surface;
              if (e.fromAt) out.available_from = e.fromAt;
              if (e.untilAt) out.available_until = e.untilAt;
              return out;
            }),
            max_round_count: body.max_round_count,
            match_duration_minutes: body.match_duration_minutes,
            break_duration_minutes: body.break_duration_minutes,
            warmup_duration_minutes: body.warmup_duration_minutes,
          } : null;
          closeSheet();
          toast("일정을 만들었어요");
          cb.onCreated(newEventId);
          if (templatePreset && cb.openSaveAsTemplatePromptSheet) {
            const promptFn = cb.openSaveAsTemplatePromptSheet;
            setTimeout(() => promptFn(templatePreset), 600);
          }
        });
      } catch (err: any) { toast(err.message); }
    },
  }, [
    templatePickerSection,
    h("div", { class: "field" }, [h("span", { class: "field__label" }, "일정명"), titleInput]),
    h("div", { class: "field" }, [h("span", { class: "field__label" }, "날짜"), dateInput]),
    // 유형 picker는 운영진/회장에게만 — 회원은 정기모임만 만들 수 있음
    // (kindHidden 기본값 "regular" 유지). 일반 회원이 이 시트를 열면 정기모임
    // 폼만 자연스럽게 보임.
    isAdmin()
      ? h("div", { class: "field" }, [
          h("span", { class: "field__label" }, "유형"),
          kindHidden,
          kindSeg,
        ])
      : kindHidden,
    friendlyToggle,
    opponentField,
    teamNamesField,
    rosterField,
    h("div", { class: "field" }, [
      h("span", { class: "field__label" }, "장소 (선택)"),
      locationInput,
    ]),
    h("div", { class: "field-row" }, [
      h("div", { class: "field field--half" }, [h("span", { class: "field__label" }, "시작 시간"), startTime.el]),
      h("div", { class: "field field--half" }, [h("span", { class: "field__label" }, ["종료 시간", buildOvernightBadge(startTime, endTime)]), endTime.el]),
    ]),
    h("div", { class: "field" }, [h("span", { class: "field__label" }, "코트"), courtListEl, addCourtBtn]),
    h("div", { class: "field" }, [h("span", { class: "field__label" }, "라운드 수"), rounds.el]),
    buildScheduleSummary(startTime, endTime, rounds, matchMin, breakMin, warmupMin),
    h("div", { class: "field" }, [advancedToggle, advancedBody]),
    submitBtn,
  ]);

  openSheet("새 일정", form);
}

export interface GenerateScheduleEvent {
  event_id: string;
  allowed_match_types?: string[] | null;
  schedule_hash?: string | null;
}

function GenerateScheduleBody({
  ev,
  regenerate,
  onGenerated,
}: {
  ev: GenerateScheduleEvent;
  regenerate: boolean;
  onGenerated: () => void;
}) {
  const initial = Array.isArray(ev.allowed_match_types) && ev.allowed_match_types.length
    ? new Set(ev.allowed_match_types)
    : new Set(["men_doubles", "women_doubles", "mixed_doubles", "exceptional_mix"]);
  const [selected, setSelected] = useState<Set<string>>(initial);

  function toggle(value: string) {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setSelected(next);
  }

  return (
    <div class="stack">
      {regenerate && (
        <p class="field__hint">재생성하면 기존 대진과 점수가 초기화돼요.</p>
      )}
      <p class="field__hint">생성할 매치 유형을 선택해주세요.</p>
      <div class="match-type-list">
        {MATCH_TYPE_OPTIONS.map((opt) => {
          const id = `mt-${opt.value}`;
          return (
            <label key={opt.value} class="match-type-item" for={id}>
              <input
                type="checkbox"
                id={id}
                class="match-type__check"
                checked={selected.has(opt.value)}
                onChange={() => toggle(opt.value)}
              />
              <div class="match-type__text">
                <div class="match-type__label">{opt.label}</div>
                {opt.hint && <div class="match-type__hint">{opt.hint}</div>}
              </div>
            </label>
          );
        })}
      </div>
      <GenerateScheduleSubmit
        ev={ev}
        regenerate={regenerate}
        selected={selected}
        onGenerated={onGenerated}
      />
    </div>
  );
}

function GenerateScheduleSubmit({
  ev,
  regenerate,
  selected,
  onGenerated,
}: {
  ev: GenerateScheduleEvent;
  regenerate: boolean;
  selected: Set<string>;
  onGenerated: () => void;
}) {
  async function onClick(e: Event) {
    const picks = Array.from(selected);
    if (picks.length === 0) { toast("하나 이상 선택해주세요"); return; }
    const btn = e.currentTarget as HTMLButtonElement;
    const callGenerate = async (overrides: { allowed_match_types?: string[]; allow_cross_team_mixed?: boolean } = {}) => {
      const reqBody: any = { allowed_match_types: overrides.allowed_match_types || picks };
      if (regenerate && ev.schedule_hash) reqBody.previous_schedule_hash = ev.schedule_hash;
      if (overrides.allow_cross_team_mixed) reqBody.allow_cross_team_mixed = true;
      return api(`/api/v1/events/${ev.event_id}/schedule/generate`, {
        method: "POST",
        body: reqBody,
      });
    };
    try {
      await withBusy(btn, "생성 중…", () => callGenerate());
      closeSheet();
      toast("스케줄이 생성되었어요");
      onGenerated();
    } catch (err: any) {
      if (err.code === "MIXED_DOUBLES_INFEASIBLE_IN_TEAM") {
        const detail = (err.details && err.details[0]) || {};
        openMixedInfeasibleDialog({
          detail,
          onRemoveMixed: async () => {
            const next = picks.filter((v) => v !== "mixed_doubles");
            if (!next.length) { toast("남복/여복 중 하나는 선택해주세요"); return; }
            try {
              toast("생성 중…");
              await callGenerate({ allowed_match_types: next });
              toast("혼복 제외하고 생성했어요");
              onGenerated();
            } catch (e2: any) { toast(e2.message); }
          },
          onAllowCrossTeam: async () => {
            try {
              toast("생성 중…");
              await callGenerate({ allow_cross_team_mixed: true });
              toast("혼복은 팀 섞어서 생성했어요");
              onGenerated();
            } catch (e2: any) { toast(e2.message); }
          },
        });
        return;
      }
      toast(err.message);
    }
  }

  return (
    <button class="btn btn--accent btn--block" type="button" onClick={onClick}>
      {regenerate ? "재생성" : "생성"}
    </button>
  );
}

export function openGenerateScheduleSheet(
  ev: GenerateScheduleEvent,
  regenerate: boolean,
  onGenerated: () => void,
): void {
  const bodyEl = mountPreact(
    <GenerateScheduleBody ev={ev} regenerate={regenerate} onGenerated={onGenerated} />,
  );
  openSheet("스케줄 생성", bodyEl);
}
